import { useEffect, useState } from 'react';
import { updateSEO, getSiteUrl } from '../../utils/seo';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    furigana: '',
    company: '',
    phone: '',
    email: '',
    inquiry_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const siteUrl = getSiteUrl();
    
    updateSEO({
      title: 'お問い合わせ | おかやま介護認定調査株式会社 - 岡山市の介護認定調査',
      description: 'おかやま介護認定調査株式会社へのお問い合わせはこちら。介護認定調査のご依頼、サービス内容、採用に関するご質問など、お気軽にご連絡ください。電話：086-012-3456（受付時間：9:00〜18:00）またはお問い合わせフォームから24時間受付中。',
      keywords: 'お問い合わせ,連絡先,電話番号,フォーム,介護認定調査,岡山市,相談,依頼',
      canonical: `${siteUrl}/contact`,
      ogType: 'website',
      schema: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'ContactPage',
            '@id': `${siteUrl}/contact#webpage`,
            url: `${siteUrl}/contact`,
            name: 'お問い合わせ | おかやま介護認定調査株式会社',
            description: 'おかやま介護認定調査株式会社へのお問い合わせページ。お電話またはフォームからお気軽にご連絡ください。',
            isPartOf: {
              '@id': `${siteUrl}/#website`
            }
          },
          {
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '介護認定調査の依頼方法を教えてください',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'お電話またはお問い合わせフォームからご連絡ください。ご依頼内容を確認させていただき、訪問日時を調整いたします。'
                }
              },
              {
                '@type': 'Question',
                name: '調査費用はどのくらいかかりますか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '介護認定調査の費用は介護保険から支払われますので、ご本人様やご家族様のご負担はございません。'
                }
              },
              {
                '@type': 'Question',
                name: '対応エリアを教えてください',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '岡山市全域（北区・中区・東区・南区）を中心に対応しております。近隣地域についてもお気軽にご相談ください。'
                }
              },
              {
                '@type': 'Question',
                name: '土日祝日の調査は可能ですか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '基本的には平日の営業時間内での調査となりますが、ご事情により土日祝日の調査が必要な場合はご相談ください。'
                }
              },
              {
                '@type': 'Question',
                name: '調査当日に必要なものはありますか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '介護保険被保険者証、お薬手帳、診察券などをご用意ください。また、日頃の様子をよくご存じのご家族の同席をお願いしております。'
                }
              },
              {
                '@type': 'Question',
                name: '急ぎの調査依頼にも対応していただけますか？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'はい、可能な限り迅速に対応させていただきます。お問い合わせから24時間以内にご連絡いたします。'
                }
              }
            ]
          }
        ]
      }
    });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.furigana || !formData.phone || !formData.email || !formData.inquiry_type || !formData.message) {
      alert('必須項目をすべて入力してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5rh8tgtu5df1co4dih0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          furigana: '',
          company: '',
          phone: '',
          email: '',
          inquiry_type: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 sm:py-24 lg:py-32 bg-gradient-to-br from-[#F5F1E8] to-[#E8F4F4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6 shadow-sm">
            <div className="w-2 h-2 bg-[#5D9B9B] rounded-full"></div>
            <span className="text-sm font-medium text-[#2C3E50]">CONTACT</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C3E50] mb-6">
            お問い合わせ
          </h1>
          <p className="text-lg sm:text-xl text-gray-700">
            ご質問、ご依頼など、お気軽にお問い合わせください
          </p>
        </div>
      </section>

      {/* Contact Methods Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#5D9B9B] rounded-2xl p-8 sm:p-12 text-white hover:shadow-xl transition-all duration-300 relative overflow-hidden group cursor-pointer">
              <div className="absolute top-8 right-8 w-12 h-12 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <i className="ri-arrow-right-up-line text-[#5D9B9B] text-xl"></i>
              </div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-phone-fill text-4xl"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                お電話でのお問い合わせ
              </h2>
              <a href="tel:086-012-3456" className="text-4xl sm:text-5xl font-bold mb-6 block hover:underline cursor-pointer">
                086-012-3456
              </a>
              <p className="text-white/90 text-lg">
                受付時間：9:00〜18:00<br />
                定休日：日曜日・祝日
              </p>
            </div>

            <div className="bg-[#2C3E50] rounded-2xl p-8 sm:p-12 text-white hover:shadow-xl transition-all duration-300 relative overflow-hidden group cursor-pointer">
              <div className="absolute top-8 right-8 w-12 h-12 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <i className="ri-arrow-right-up-line text-[#2C3E50] text-xl"></i>
              </div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-mail-fill text-4xl"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                フォームでのお問い合わせ
              </h2>
              <p className="text-3xl font-bold mb-6">
                24時間受付
              </p>
              <p className="text-white/90 text-lg">
                下記のフォームからお問い合わせください。<br />
                2営業日以内にご返信いたします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2C3E50] text-center mb-12">
            お問い合わせフォーム
          </h2>

          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-center font-medium">
                  お問い合わせを受け付けました。ご連絡ありがとうございます。
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-center font-medium">
                  送信に失敗しました。もう一度お試しください。
                </p>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5D9B9B] focus:border-transparent transition-all duration-300"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="furigana" className="block text-sm font-medium text-gray-700 mb-2">
                  フリガナ <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="furigana"
                  name="furigana"
                  value={formData.furigana}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5D9B9B] focus:border-transparent transition-all duration-300"
                  placeholder="ヤマダ タロウ"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  会社名・施設名
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5D9B9B] focus:border-transparent transition-all duration-300"
                  placeholder="株式会社○○"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5D9B9B] focus:border-transparent transition-all duration-300"
                  placeholder="086-012-3456"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5D9B9B] focus:border-transparent transition-all duration-300"
                  placeholder="example@example.com"
                />
              </div>

              <div>
                <label htmlFor="inquiry_type" className="block text-sm font-medium text-gray-700 mb-2">
                  お問い合わせ種別 <span className="text-red-500">*</span>
                </label>
                <select
                  id="inquiry_type"
                  name="inquiry_type"
                  value={formData.inquiry_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5D9B9B] focus:border-transparent transition-all duration-300 cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="介護認定調査のご依頼">介護認定調査のご依頼</option>
                  <option value="サービス内容について">サービス内容について</option>
                  <option value="採用について">採用について</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5D9B9B] focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <p className="text-sm text-gray-500 mt-2 text-right">
                  {formData.message.length} / 500文字
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#2C3E50] text-white rounded-lg font-medium hover:bg-[#1a252f] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2C3E50] text-center mb-12">
            よくある質問
          </h2>

          <div className="space-y-4">
            {[
              {
                question: '介護認定調査の依頼方法を教えてください',
                answer: 'お電話またはお問い合わせフォームからご連絡ください。ご依頼内容を確認させていただき、訪問日時を調整いたします。'
              },
              {
                question: '調査費用はどのくらいかかりますか？',
                answer: '介護認定調査の費用は介護保険から支払われますので、ご本人様やご家族様のご負担はございません。'
              },
              {
                question: '対応エリアを教えてください',
                answer: '岡山市全域（北区・中区・東区・南区）を中心に対応しております。近隣地域についてもお気軽にご相談ください。'
              },
              {
                question: '土日祝日の調査は可能ですか？',
                answer: '基本的には平日の営業時間内での調査となりますが、ご事情により土日祝日の調査が必要な場合はご相談ください。'
              },
              {
                question: '調査当日に必要なものはありますか？',
                answer: '介護保険被保険者証、お薬手帳、診察券などをご用意ください。また、日頃の様子をよくご存じのご家族の同席をお願いしております。'
              },
              {
                question: '急ぎの調査依頼にも対応していただけますか？',
                answer: 'はい、可能な限り迅速に対応させていただきます。お問い合わせから24時間以内にご連絡いたします。'
              }
            ].map((faq, index) => (
              <details
                key={index}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden group"
              >
                <summary className="px-6 py-5 cursor-pointer flex items-center justify-between font-medium text-[#2C3E50] hover:bg-gray-50 transition-colors duration-300">
                  <span className="flex-1 pr-4">{faq.question}</span>
                  <i className="ri-add-line text-2xl text-[#5D9B9B] group-open:rotate-45 transition-transform duration-300"></i>
                </summary>
                <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
